import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { LoadingState } from '../ui/LoadingState';
import { ArchiveEmptyState } from './ArchiveEmptyState';
import { MemoryMarkerDialog } from './MemoryMarkerDialog';
import { MemoryMarkerCard } from './MemoryMarkerCard';
import { PricelessPossessionsSection } from './PricelessPossessionsSection';
import { ArchiveHelperText } from './ArchiveHelperText';
import {
  CONNECTED_MEMORIES_LABEL,
  deleteMemoryMarker,
  fetchMemoryMarkersForTree,
  filterNonPossessions,
  getMarkerTypeIcon,
  getMarkerTypeStyle,
  groupMarkersByType,
  MARKER_TYPE_LABELS,
  MARKER_TYPE_ORDER,
  POSSESSION_MARKER_TYPES,
  MEMORY_MARKERS_UI_LABEL,
} from '../../utils/memoryMarkers';
import { isGenealogicalPerson } from '../../utils/companionProfiles';
import { toast } from 'sonner';
import { humanizeError } from '../../utils/errorHandling';

/**
 * Tree-level browse view for People, places & things.
 */
export function MemoryMarkersWorkspace({ treeId, people = [], canEdit = false, onMarkersChange }) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(Boolean(treeId));
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);

  const genealogicalPeople = useMemo(
    () => (people || []).filter((p) => isGenealogicalPerson(p)),
    [people]
  );

  const peopleById = useMemo(() => {
    const map = {};
    for (const p of people) map[p.id] = p;
    return map;
  }, [people]);

  const loadMarkers = useCallback(async () => {
    if (!treeId) {
      setMarkers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchMemoryMarkersForTree(treeId);
      setMarkers(rows);
    } catch {
      setMarkers([]);
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    loadMarkers();
  }, [loadMarkers]);

  const filtered = useMemo(() => {
    const base = filterNonPossessions(markers);
    if (typeFilter === 'all') return base;
    return base.filter((m) => m.marker_type === typeFilter);
  }, [markers, typeFilter]);

  const grouped = groupMarkersByType(filtered);

  const handleSaved = async () => {
    await loadMarkers();
    onMarkersChange?.();
  };

  const handleDelete = async (marker) => {
    if (!window.confirm(`Remove "${marker.title}" from connected memories?`)) return;
    try {
      await deleteMemoryMarker(marker.id);
      toast.success('Connected memory removed.');
      await loadMarkers();
      onMarkersChange?.();
    } catch (err) {
      toast.error(humanizeError(err));
    }
  };

  const anchorPersonFor = (marker) => {
    const pid = marker?.attachment?.person_id;
    return pid ? peopleById[pid] : null;
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8" data-testid="memory-markers-workspace">
      <header className="mb-6 space-y-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-serif text-2xl font-light tracking-tight text-[#1c1917]">
              {MEMORY_MARKERS_UI_LABEL}
            </h2>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-[#5c534a]">
              {CONNECTED_MEMORIES_LABEL} across your archive — places, objects, milestones and events that
              shape the story without joining the family tree chart.
            </p>
          </div>
          {canEdit ? (
            <Button
              type="button"
              className="shrink-0 rounded-full bg-primary text-primary-foreground"
              onClick={() => {
                setEditingMarker(null);
                setDialogOpen(true);
              }}
              data-testid="workspace-add-memory-btn"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add a connected memory
            </Button>
          ) : null}
        </div>
        <ArchiveHelperText>
          These are memory anchors — not people on the tree. Dated moments also appear on Timeline.
        </ArchiveHelperText>
      </header>

      <PricelessPossessionsSection
        treeId={treeId}
        people={people}
        canEdit={canEdit}
        onChange={handleSaved}
      />

      <div className="mb-4 mt-8">
        <h3 className="font-serif text-lg text-[#1c1917]">Places &amp; moments</h3>
        <p className="mt-1 text-sm text-muted-foreground">Homes, milestones and events — memory anchors beyond keepsakes.</p>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <Button
          type="button"
          size="sm"
          variant={typeFilter === 'all' ? 'secondary' : 'ghost'}
          className="h-7 rounded-full text-xs"
          onClick={() => setTypeFilter('all')}
        >
          All
        </Button>
        {MARKER_TYPE_ORDER.filter((type) => !POSSESSION_MARKER_TYPES.includes(type)).map((type) => {
          const Icon = getMarkerTypeIcon(type);
          const style = getMarkerTypeStyle(type);
          return (
            <Button
              key={type}
              type="button"
              size="sm"
              variant={typeFilter === type ? 'secondary' : 'ghost'}
              className="h-7 rounded-full text-xs gap-1.5"
              onClick={() => setTypeFilter(type)}
            >
              <Icon className={`h-3 w-3 ${typeFilter === type ? style.iconText : 'opacity-60'}`} />
              {MARKER_TYPE_LABELS[type]}
            </Button>
          );
        })}
      </div>

      {loading ? <LoadingState message="Loading connected memories…" /> : null}

      {!loading && filtered.length === 0 ? (
        <ArchiveEmptyState
          icon={Filter}
          align="start"
          title={typeFilter === 'all' ? 'Places and moments will appear here' : `No ${MARKER_TYPE_LABELS[typeFilter]?.toLowerCase() || 'matching'} yet`}
          description={
            typeFilter === 'all'
              ? 'Add a home, milestone or event — memory anchors that shape the story beyond the family tree chart.'
              : `Add a ${MARKER_TYPE_LABELS[typeFilter]?.toLowerCase() || 'memory anchor'} when you are ready.`
          }
          actions={
            canEdit && typeFilter === 'all' ? (
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setDialogOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add a connected memory
              </Button>
            ) : null
          }
        />
      ) : null}

      {!loading && grouped.length > 0 ? (
        <div className="space-y-8">
          {grouped.map(({ type, label, items }) => {
            const style = getMarkerTypeStyle(type);
            const Icon = getMarkerTypeIcon(type);
            return (
              <section key={type}>
                <div className="mb-3 flex items-center gap-2">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${style.iconBg}`}>
                    <Icon className={`h-4 w-4 ${style.iconText}`} aria-hidden />
                  </div>
                  <h3 className="font-serif text-lg text-[#1c1917]">{label}</h3>
                  <span className="text-xs text-muted-foreground">({items.length})</span>
                </div>
                <ul className="space-y-3">
                  {items.map((marker) => (
                    <MemoryMarkerCard
                      key={marker.id}
                      marker={marker}
                      canEdit={canEdit}
                      onEdit={(m) => {
                        setEditingMarker(m);
                        setDialogOpen(true);
                      }}
                      onDelete={handleDelete}
                      linkedPerson={anchorPersonFor(marker)}
                      variant="workspace"
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : null}

      {canEdit ? (
        <MemoryMarkerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          treeId={treeId}
          marker={editingMarker}
          allowAttachmentPick
          genealogicalPeople={genealogicalPeople}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
}

export default MemoryMarkersWorkspace;
